import React, {useCallback, useRef, useState} from 'react';

import Icon, {Icons} from '@pb/kit-component-icon';
import IconButton from '@pb/kit-component-icon-button';

import {
    dropZone,
    dropZoneActive,
    dropZoneHint,
    dropZoneIcon,
    dropZoneText,
    errorText,
    fileIndicator,
    fileName as fileNameStyle,
} from '../upload-metadata.module.styl';

type XmlDropZoneProps = {
    onFileSelect: (file: File) => void,
    onRemove: () => void,
    error: string | null,
    fileName: string | null,
};

export const XmlDropZone: React.FC<XmlDropZoneProps> = ({onFileSelect, onRemove, error, fileName}) => {
    const [dragOver, setDragOver] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const dragDepthRef = useRef(0);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) {
            setDragOver(false);
        }
    }, []);

    const handleDragEnter = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        dragDepthRef.current += 1;
        setDragOver(true);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        dragDepthRef.current = 0;
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            onFileSelect(file);
        }
    }, [onFileSelect]);

    const handleClick = useCallback(() => {
        inputRef.current?.click();
    }, []);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            onFileSelect(file);
        }
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    }, [onFileSelect]);

    if (fileName != null && error == null) {
        return (
            <div>
                <div className={fileIndicator}>
                    <span className={fileNameStyle}>{fileName}</span>
                    <IconButton
                        onClick={onRemove}
                        iconProps={{type: Icons.CROSS, size: 'Size_14'}}
                    />
                </div>
            </div>
        );
    }

    return (
        <div>
            <div
                className={`${dropZone} ${dragOver ? dropZoneActive : ''}`}
                onClick={handleClick}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        handleClick();
                    }
                }}>
                <input
                    ref={inputRef}
                    type="file"
                    accept=".xml"
                    onChange={handleInputChange}
                    style={{display: 'none'}}
                />
                <Icon type={Icons.PLUS_CIRCLE} size="Size_24" className={dropZoneIcon}/>
                <span className={dropZoneText}>Drag &amp; drop your metadata XML file here</span>
                <span className={dropZoneHint}>or click to browse &middot; XML files only</span>
            </div>
            {error != null ? <span className={errorText}>{error}</span> : null}
        </div>
    );
};
